import { ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, OnDestroy, OnInit, viewChild } from '@angular/core';
import { BomItemAttribute, BomTreeState, BomTreeStore, getSelectedNode } from './bom-tree.store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter, map, Observable } from 'rxjs';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { BomTreeNodeComponent } from './components/bom-tree-node/bom-tree-node.component';
import { AddNodeDropdownComponent, BomItemSelected } from '../../add-node-dropdown/add-node-dropdown.component';
import { select } from '../../../utils/select';
import { Route } from '../../../utils/route';
import { formatAmount, formatAmountInput } from '../../../utils/format-amount';
import { ListItem, TreeSummaryComponent, TYPE_LABELS } from '../../tree-summary/tree-summary.component';
import { BomTreeItemVm, BomTreeVm } from './models/bom-tree-item.view-model';
import { WhereUsedComponent } from './components/where-used/where-used.component';
import { BomItemOrdersComponent } from './components/bom-item-orders/bom-item-orders.component';
import { BomItemStagesComponent } from './components/bom-item-stages/bom-item-stages.component';
import { ATTACHMENT_ACCEPT, AttachmentsChange, BomAttachmentsComponent } from './components/bom-attachments/bom-attachments.component';
import { BomNotesComponent } from './components/bom-notes/bom-notes.component';
import { ContextMenuComponent, ContextMenuItem } from '../../context-menu/context-menu.component';
import { UNIT_LABELS, UNITS } from '../../../models/units';
import { BOM_STRUCTURES, STRUCTURE_LABELS } from '../../../models/bom-structure';
import { ItemPhotoComponent } from '../../item-photo/item-photo.component';

@Component({
  selector: 'bom-tree-page',
  templateUrl: './bom-tree.page.html',
  styleUrl: './bom-tree.page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, NgTemplateOutlet, AddNodeDropdownComponent, BomTreeNodeComponent, TreeSummaryComponent, WhereUsedComponent, BomItemOrdersComponent, BomItemStagesComponent, BomAttachmentsComponent, BomNotesComponent, ContextMenuComponent, ItemPhotoComponent]
})
export class BomTreePage implements OnInit, OnDestroy {

  readonly typeLabels = TYPE_LABELS;
  readonly attachmentAccept = ATTACHMENT_ACCEPT;
  readonly units = UNITS;
  readonly unitLabels = UNIT_LABELS;
  readonly structures = BOM_STRUCTURES;
  readonly structureLabels = STRUCTURE_LABELS;
  readonly formatAmount = formatAmount;
  readonly formatAmountInput = formatAmountInput;

  // Only one card field is open at a time, so every field shares one ref (the overseer headerEditInput precedent)
  readonly attributeInput = viewChild<ElementRef<HTMLInputElement | HTMLSelectElement>>('attributeInput');

  // the card headers' icons stand before the tree does (0127); the components they drive exist only once it has loaded
  readonly notes = viewChild(BomNotesComponent);
  readonly attachments = viewChild(BomAttachmentsComponent);

  // DRZEWO's shadow while the tree loads (0127): one root line and five children
  readonly shadowNodes = [0, 1, 2, 3, 4, 5];

  readonly contextMenuItems: ContextMenuItem[] = [
    { id: 'add-child', label: 'Dodaj element' },
    { id: 'remove-node', label: 'Usuń element' }
  ];

  readonly state$: Observable<BomTreeState>;

  readonly rootBomItemId$: Observable<number | 'new' | undefined>;
  readonly title$: Observable<string | undefined>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: BomTreeStore,
    private destroyRef: DestroyRef
  ) {
    this.state$ = this.store.state$;

    this.rootBomItemId$ = this.state$.pipe(select(state => state.rootBomItemId));
    this.title$ = this.state$.pipe(select(state => state.root?.data.name));

    // Focus the freshly opened field; an input comes prefilled, select it so typing replaces it
    effect(() => {
      const input = this.attributeInput();

      if (input) {
        input.nativeElement.focus();

        if (input.nativeElement instanceof HTMLInputElement) {
          input.nativeElement.select();
        }
      }
    });
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const id = Route.parseId(params.get('id'));

      console.log('BomTreeStore', { id });

      // in-app navigation between /bom/{id} pages reuses this component — re-init only on an actual id change
      // (also skips the replaceUrl redirect from /bom/new to the freshly created id)
      if (id === this.store.state.rootBomItemId) {
        return;
      }

      this.store.init(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        console.log('BomTreePage initialized', { state: this.store.state });
      });
    });

    this.initParams();
  }

  selectedNode(state: BomTreeState): BomTreeItemVm {
    return getSelectedNode(state);
  }

  isEditing(state: BomTreeState, field: BomItemAttribute): boolean {
    return state.attributeEdit?.field === field;
  }

  startAttributeEdit(field: BomItemAttribute): void {
    this.store.startAttributeEdit(field);
  }

  saveAttribute(field: BomItemAttribute, value: string): void {
    this.store.saveAttribute(field, value).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  cancelAttributeEdit(): void {
    this.store.cancelAttributeEdit();
  }

  selectedBomItemId(state: BomTreeState): number | undefined {
    return this.selectedNode(state).data.bomItemId;
  }

  // undefined for the tree's root — ETAPY then lists the item under every parent it is used in
  selectedParentBomItemId(state: BomTreeState): number | undefined {
    return BomTreeVm.getParentNodeById(state.root!, this.selectedNode(state).id)?.data.bomItemId;
  }

  // the selected node's role in the FULL tree — a scoped subtree root is not automatically a product; a purchased node
  // is the material wherever it sits, as tree-summary counts it (0120)
  selectedRootType(state: BomTreeState): ListItem['type'] {
    const selectedNode = this.selectedNode(state);

    if (selectedNode.data.structure === 'Purchased') {
      return 'material';
    }

    if (selectedNode.id === state.root!.id) {
      return 'product';
    }

    return selectedNode.children.length === 0 ? 'material' : 'module';
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }

  onAddNodeDropdownClose(): void {
    this.store.closeAddNodeDropdown();
  }

  onContextMenuAction(actionId: string): void {
    const contextMenu = this.store.state.contextMenu!;

    if (actionId === 'add-child') {
      this.store.openAddNodeDropdown(contextMenu.nodeId, contextMenu.position);
    }

    if (actionId === 'remove-node') {
      // delete runs at page level, so the request survives the node component being destroyed by the tree update
      this.store.deleteItem(contextMenu.nodeId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }

  onContextMenuClose(): void {
    this.store.closeContextMenu();
  }

  onNoteAdded(bomItemId: number): void {
    this.store.setItemHasNotes(bomItemId, true);
  }

  // the full-size photo, the same URL a ZDJĘCIA row links to
  photoContentUrl(attachmentId: number): string {
    return `/api/bom/attachments/${attachmentId}/content`;
  }

  onAttachmentsChanged(change: AttachmentsChange): void {
    this.store.setItemAttachments(change.bomItemId, change);
  }

  onAddNodeBomItemSelected(item: BomItemSelected): void {
    this.store.addChildFromDropdownItem(item).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private initParams(): void {
    this.rootBomItemId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(rootBomItemId => {
      if (!rootBomItemId) {
        return;
      }

      this.router.navigate(['../', rootBomItemId], {
        relativeTo: this.route,
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    });
  }
}
